import { type I18nLanguage, type localeConfigurations } from '@/i18n';
import type { BackgroundTheme, BrandColors } from '@/types/color';
import type { TenantOption } from '@/types/tenant';
import {
  AGUIA_BRAND_COLORS,
  ARENAWAYS_BRAND_COLORS,
  TURNIT_BRAND_COLORS,
  TWILINER_BRAND_COLORS,
  VELVET_BRAND_COLORS,
} from '@/utils/color';
import type { StopsSearchingStrategy } from '@turnit-ride-ui/webshop-search-widget';
import type { Country } from 'react-phone-number-input';

export const TENANT_OPTIONS = [
  {
    value: 'turnit',
    label: 'Turnit',
  },
  {
    value: 'arenaways',
    label: 'Arenaways',
  },
  {
    value: 'twiliner',
    label: 'Twiliner',
  },
  {
    value: 'velvet',
    label: 'Velvet',
  },
  {
    value: 'aguia',
    label: 'Aguia',
  },
] as const;

const HOSTNAME_TENANT_MAP: Array<{
  hostnameIncludes: string;
  tenant: TenantOption['value'];
}> = [
  {
    hostnameIncludes: 'webshop-engine.demos.sandbox-brazil.turnit.tech',
    tenant: 'aguia',
  },
];

const FALLBACK_TENANT_VALUE: TenantOption['value'] = 'aguia';

export const resolveTenantByValue = (value?: string) =>
  value ? TENANT_OPTIONS.find((option) => option.value === value) : undefined;

export const getInitialTenant = () => {
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : undefined;

  const hostMatchedTenant = hostname
    ? HOSTNAME_TENANT_MAP.find(({ hostnameIncludes }) =>
        hostname.includes(hostnameIncludes)
      )?.tenant
    : undefined;

  return (
    resolveTenantByValue(hostMatchedTenant) ??
    resolveTenantByValue(import.meta.env.VITE_APP_TENANT) ??
    resolveTenantByValue(FALLBACK_TENANT_VALUE) ??
    TENANT_OPTIONS[0]
  );
};

export const getTenantConfiguration = (
  tenant: TenantOption['value'] | string
):
  | {
      language: I18nLanguage;
      languageOptions: Array<keyof typeof localeConfigurations>;
      defaultCountry: Country;
      webPageUrl?: string;
      showTravelPassDisclaimer: boolean;
      showSuccessMessage: boolean;
      showPayWithExternalPayment: boolean;
      showPayWithAdyen: boolean;
      stopsSearchingStrategy: StopsSearchingStrategy;
      brandColors: BrandColors;
      backgroundTheme: BackgroundTheme;
      showLogin: boolean;
    }
  | undefined => {
  switch (tenant) {
    case 'turnit':
      return {
        language: 'en',
        languageOptions: ['en', 'it', 'de'],
        defaultCountry: 'EE',
        showTravelPassDisclaimer: false,
        showSuccessMessage: true,
        showPayWithExternalPayment: true, // DO NOT ENABLE FOR LIVE CUSTOMER
        showPayWithAdyen: false,
        stopsSearchingStrategy: 'dynamic',
        brandColors: TURNIT_BRAND_COLORS,
        backgroundTheme: 'dark',
        showLogin: true,
      };
    case 'arenaways':
      return {
        language: 'it',
        languageOptions: ['en', 'it'],
        defaultCountry: 'IT',
        webPageUrl: 'https://arenaways.it/',
        showTravelPassDisclaimer: true,
        showSuccessMessage: true,
        showPayWithExternalPayment: false, // DO NOT ENABLE FOR LIVE CUSTOMER
        showPayWithAdyen: true,
        stopsSearchingStrategy: 'static',
        brandColors: ARENAWAYS_BRAND_COLORS,
        backgroundTheme: 'white',
        showLogin: false,
      };
    case 'twiliner':
      return {
        language: 'en',
        languageOptions: ['en', 'de'],
        defaultCountry: 'CH',
        webPageUrl: 'https://twiliner.com/',
        showTravelPassDisclaimer: false,
        showSuccessMessage: true,
        showPayWithExternalPayment: false, // DO NOT ENABLE FOR LIVE CUSTOMER
        showPayWithAdyen: true,
        stopsSearchingStrategy: 'static',
        brandColors: TWILINER_BRAND_COLORS,
        backgroundTheme: 'dark',
        showLogin: false,
      };
    case 'velvet':
      return {
        language: 'en',
        languageOptions: ['en', 'it', 'de'],
        defaultCountry: 'EE',
        webPageUrl: 'https://www.velvet.fr/',
        showTravelPassDisclaimer: false,
        showSuccessMessage: true,
        showPayWithExternalPayment: false, // DO NOT ENABLE FOR LIVE CUSTOMER
        showPayWithAdyen: false,
        stopsSearchingStrategy: 'dynamic',
        brandColors: VELVET_BRAND_COLORS,
        backgroundTheme: 'dark',
        showLogin: true,
      };
    case 'aguia':
      return {
        language: 'pt',
        languageOptions: ['pt', 'en'],
        defaultCountry: 'BR',
        webPageUrl: 'https://www.aguiabranca.com.br/',
        showTravelPassDisclaimer: false,
        showSuccessMessage: true,
        showPayWithExternalPayment: false, // DO NOT ENABLE FOR LIVE CUSTOMER
        showPayWithAdyen: false,
        stopsSearchingStrategy: 'dynamic',
        brandColors: AGUIA_BRAND_COLORS,
        backgroundTheme: 'light',
        showLogin: true,
      };
    default:
      return;
  }
};
